import { useState } from "react";
import { Link, useLocation } from "react-router";
import { Minus, Plus, ShoppingBasket } from "lucide-react";
import { formatWeight, isSoldByWeight, weightPriceCents } from "@tarawa/shared";
import { cn } from "@tarawa/ui";
import { useCart } from "@/lib/cart";
import { useI18n } from "@/lib/i18n";
import { useToast } from "@/lib/toast";
import type { Product } from "@/lib/types";

/** Router state on product links: the listing (path + query) the product was opened from. */
export interface ProductLinkState {
  from?: string;
}

const TINTS = ["bg-brand-50", "bg-saffron-50", "bg-terracotta-50", "bg-[#eef4fb]", "bg-[#f3effa]"];

/** Product photo, or a soft branded placeholder when the product has no image yet. */
export function ProductImage({
  src,
  alt,
  seed = "",
  className,
}: {
  src: string | null | undefined;
  alt: string;
  seed?: string;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const tint = TINTS[[...seed].reduce((s, c) => s + c.charCodeAt(0), 0) % TINTS.length];
  if (!src || failed) {
    return (
      <div className={cn("flex items-center justify-center", tint, className)} role="img" aria-label={alt}>
        <span className="text-3xl font-extrabold text-ink/15 select-none">{alt.trim().charAt(0).toUpperCase()}</span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className={cn("object-cover", className)}
    />
  );
}

export function QuantityStepper({
  value,
  onChange,
  size = "md",
  max = 50,
  className,
}: {
  value: number;
  onChange: (v: number) => void;
  size?: "sm" | "md" | "lg";
  max?: number;
  className?: string;
}) {
  const { t } = useI18n();
  const btn = cn(
    "flex items-center justify-center rounded-full transition active:scale-90 disabled:opacity-40",
    size === "sm" && "size-8",
    size === "md" && "size-10",
    size === "lg" && "size-12",
  );
  return (
    <div className={cn("flex items-center gap-1 rounded-full bg-brand-50 p-1", className)}>
      <button type="button" className={cn(btn, "bg-surface text-brand-700 shadow-sm")} onClick={() => onChange(value - 1)} aria-label={t("product.decrease")}>
        <Minus className={size === "sm" ? "size-4" : "size-5"} />
      </button>
      <span
        className={cn("ltr-nums min-w-8 text-center font-bold tabular-nums text-brand-900", size === "lg" ? "text-lg" : "text-base")}
        aria-live="polite"
      >
        {value}
      </span>
      <button
        type="button"
        className={cn(btn, "bg-brand-600 text-white shadow-sm")}
        onClick={() => onChange(value + 1)}
        disabled={value >= max}
        aria-label={t("product.increase")}
      >
        <Plus className={size === "sm" ? "size-4" : "size-5"} />
      </button>
    </div>
  );
}

/** Hook wrapping cart updates with user feedback (toasts on error). */
export function useCartActions() {
  const cart = useCart();
  const toast = useToast();
  const i18n = useI18n();
  const set = async (productId: string, quantity: number, weightGrams?: number) => {
    try {
      await cart.setQuantity(productId, quantity, weightGrams);
    } catch (err) {
      toast(i18n.errorMessage(err), "error");
    }
  };
  return { set, quantityOf: cart.quantityOf, weightOf: cart.weightOf };
}

/**
 * Weights offered by the Admin, each with its price (proportional to the base weight and price).
 * `wrap` puts the options on several lines instead of a scrolling row (narrow columns).
 */
export function WeightBar({
  product,
  selected,
  onSelect,
  wrap,
}: {
  product: Product & { baseWeightGrams: number };
  selected: number;
  onSelect: (grams: number) => void;
  wrap?: boolean;
}) {
  const { money, t, locale } = useI18n();
  return (
    <div
      role="group"
      aria-label={t("product.weight")}
      className={wrap ? "flex flex-wrap gap-1.5" : "no-scrollbar -mx-1 flex gap-1 overflow-x-auto px-1"}
    >
      {product.weightOptionsGrams!.map((g) => {
        const active = g === selected;
        return (
          <button
            key={g}
            type="button"
            onClick={() => onSelect(g)}
            aria-pressed={active}
            className={cn(
              "shrink-0 rounded-full border px-2 py-1 text-[11px] leading-4 font-semibold transition",
              active ? "border-brand-600 bg-brand-50 text-brand-800" : "border-line bg-surface text-muted hover:text-ink",
            )}
          >
            <span className="ltr-nums block">{formatWeight(g, locale)}</span>
            <span className="ltr-nums block font-bold">{money(weightPriceCents(product.priceCents, product.baseWeightGrams, g))}</span>
          </button>
        );
      })}
    </div>
  );
}

export function ProductCard({ product }: { product: Product }) {
  const { ln, money, t, locale } = useI18n();
  const { set, quantityOf, weightOf } = useCartActions();
  const qty = quantityOf(product.id);
  const name = ln(product.name, product.nameAr);
  const byWeight = isSoldByWeight({ ...product, baseWeightGrams: product.baseWeightGrams ?? null, weightOptionsGrams: product.weightOptionsGrams ?? [] });
  const weight = weightOf(product.id) ?? product.baseWeightGrams ?? 0;
  const { pathname, search } = useLocation();
  const linkState: ProductLinkState = { from: pathname + search };

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-[var(--radius-card)] bg-surface shadow-[var(--shadow-card)] transition-shadow hover:shadow-lg">
      <Link to={`/products/${product.id}`} state={linkState} className="block" aria-label={name}>
        <div className="relative aspect-square overflow-hidden">
          <ProductImage
            src={product.imageUrl}
            alt={name}
            seed={product.id}
            className={cn("size-full transition-transform duration-300 group-hover:scale-[1.03]", !product.isAvailable && "opacity-50 grayscale")}
          />
          {!product.isAvailable && (
            <span className="absolute start-2 top-2 rounded-full bg-ink/80 px-2.5 py-1 text-[11px] font-semibold text-white">
              {t("common.unavailable")}
            </span>
          )}
        </div>
      </Link>
      <div className="flex flex-1 flex-col gap-2 p-3">
        <Link to={`/products/${product.id}`} state={linkState} className="line-clamp-2 min-h-10 text-sm leading-5 font-semibold text-ink">
          {name}
        </Link>
        <div className="mt-auto flex items-center justify-between gap-2">
          <span className="ltr-nums text-base font-extrabold text-brand-800">
            {money(product.priceCents)}
            {byWeight && <span className="block text-[11px] leading-3 font-semibold text-muted">/ {formatWeight(product.baseWeightGrams!, locale)}</span>}
          </span>
          {product.isAvailable &&
            (qty > 0 ? (
              <QuantityStepper size="sm" value={qty} onChange={(v) => set(product.id, v)} />
            ) : (
              <button
                onClick={() => set(product.id, 1)}
                className="flex size-10 items-center justify-center rounded-full bg-brand-600 text-white shadow-sm transition hover:bg-brand-700 active:scale-90"
                aria-label={`${t("product.addToCart")} — ${name}`}
              >
                <Plus className="size-5" />
              </button>
            ))}
        </div>
        {byWeight && (product.weightOptionsGrams?.length ?? 0) > 0 && product.isAvailable && qty > 0 && (
          <WeightBar product={product as Product & { baseWeightGrams: number }} selected={weight} onSelect={(g) => set(product.id, qty, g)} />
        )}
      </div>
    </div>
  );
}

export function ProductGrid({ products }: { products: Product[] }) {
  const { t } = useI18n();
  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center py-12 text-center text-muted">
        <ShoppingBasket className="mb-2 size-8" />
        {t("categories.empty")}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4 xl:grid-cols-5">
      {products.map((p) => (
        <ProductCard key={p.id} product={p} />
      ))}
    </div>
  );
}

export function ProductGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4 xl:grid-cols-5" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="overflow-hidden rounded-[var(--radius-card)] bg-surface shadow-[var(--shadow-card)]">
          <div className="aspect-square animate-pulse bg-canvas" />
          <div className="space-y-2 p-3">
            <div className="h-3.5 w-3/4 animate-pulse rounded bg-canvas" />
            <div className="h-5 w-1/3 animate-pulse rounded bg-canvas" />
          </div>
        </div>
      ))}
    </div>
  );
}
